import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { api, errorText, fmtBytes } from "../../lib/api.ts";
import { useApi } from "../../lib/useApi.ts";
import { EmailCode } from "./EmailCode.tsx";
import { SkyBand, usePeriod } from "./Sky.tsx";
import { Requirements, STEPS } from "./Steps.tsx";

interface Image { file: string; size: number; uploaded: string; sha256: string | null }
interface Files { files: Image[] }
interface Me { user: { email: string } }

/** "iso/atzmai-os-0.2-20261023.iso": its version and day, when the name says them. */
export function describe(image: Image) {
  const name = image.file.replace(/^iso\//, "");
  const m = /^atzmai-os-(\d+(?:\.\d+)*)[-_](\d{4})(\d{2})(\d{2})/.exec(name);
  const day = m ? new Date(`${m[2]}-${m[3]}-${m[4]}T12:00:00Z`) : new Date(image.uploaded);
  return {
    name,
    version: m ? m[1] : null,
    day: day.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }),
  };
}

type State = "done" | "now" | "later";

export function Download() {
  const period = usePeriod();
  const files = useApi<Files>("/api/account/downloads");
  const me = useApi<Me>("/api/account/me");
  const [started, setStarted] = useState(false);
  const signedOut = files.error?.status === 401;
  const signedIn = Boolean(files.data);
  // one image is offered at a time: the newest, should another linger
  const current = [...(files.data?.files ?? [])].sort((a, b) => b.uploaded.localeCompare(a.uploaded))[0];
  const about = current ? describe(current) : null;

  const step = (n: number): State => {
    const at = !signedIn ? 1 : started ? 3 : 2;
    return n < at ? "done" : n === at ? "now" : "later";
  };

  return (
    <div className="flex flex-col gap-20">
      <SkyBand period={period} short />

      <header className="on-sky flex max-w-[40rem] flex-col gap-4 pt-4 md:pt-10">
        <h1 className="text-[clamp(38px,5.6vw,60px)] leading-[1.05] tracking-[-.02em]">
          Download Atzmai OS{about?.version ? ` ${about.version}` : ""}
        </h1>
        <p className="text-[20px] leading-snug text-ink">
          Free, for any 64-bit PC or laptop. Try it from a USB stick first, and install it when you're ready.
        </p>
      </header>

      <ol className="journey m-0 flex list-none flex-col p-0" aria-label="From here to a running computer">
        <Step n={1} state={step(1)} title={signedIn ? "Email confirmed" : "Confirm your email"}>
          {files.loading && !files.data && !signedOut ? (
            <div className="h-24 max-w-md animate-pulse rounded-xl bg-glass" aria-label="Loading" />
          ) : signedIn ? (
            <p className="text-ink-2">
              Downloading as <b className="font-medium text-ink">{me.data?.user.email ?? "you"}</b>. <Link to="/account">Your account</Link>
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="max-w-[34rem] text-ink-2">
                We send a code to check it's yours, and that's all we use it for. Verified before? <Link to="/signin">Sign in</Link>.
              </p>
              <EmailCode mode="signup" onDone={() => { void files.reload(); void me.reload(); }} />
            </div>
          )}
        </Step>

        <Step n={2} state={step(2)} title="Download the image">
          {files.error && !signedOut ? (
            <p role="alert" className="text-bad">{files.error.message}</p>
          ) : !signedIn ? (
            <p className="text-ink-3">One file, about 2.8 GB. It's ready as soon as your email is confirmed.</p>
          ) : current ? (
            <Release image={current} onStart={() => setStarted(true)} />
          ) : (
            <p className="text-ink-2">No image is published right now. The next release is on its way, and your account is ready for it.</p>
          )}
        </Step>

        <Step n={3} state={step(3)} title={STEPS[1]!.title}>
          <p className="text-ink-2">{STEPS[1]!.body}</p>
        </Step>

        <Step n={4} state="later" title={STEPS[2]!.title} last>
          <p className="text-ink-2">{STEPS[2]!.body}</p>
        </Step>
      </ol>

      <section className="flex flex-col gap-5">
        <h2 className="text-[clamp(22px,2.6vw,28px)]">What the computer needs</h2>
        <Requirements />
      </section>
    </div>
  );
}

function Step({ n, state, title, last = false, children }: { n: number; state: State; title: string; last?: boolean; children: ReactNode }) {
  return (
    <li className="journey-step grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-5 md:grid-cols-[3.25rem_minmax(0,1fr)] md:gap-x-8" data-state={state} aria-current={state === "now" ? "step" : undefined}>
      <div className="flex flex-col items-center">
        <span className="journey-mark" aria-hidden="true">
          {state === "done" ? (
            <svg viewBox="0 0 20 20" width="20" height="20"><path d="M5 10.5l3.2 3.2L15 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          ) : n}
        </span>
        {!last && <span className="journey-line" aria-hidden="true" />}
      </div>
      <div className={`flex min-w-0 flex-col gap-3 ${last ? "" : "pb-14"}`}>
        <h2 className="journey-title mt-1.5 text-[clamp(22px,2.6vw,28px)] leading-tight">
          <span className="sr-only">{state === "done" ? "Done: " : state === "now" ? "Now: " : ""}</span>{title}
        </h2>
        {children}
      </div>
    </li>
  );
}

function Release({ image, onStart }: { image: Image; onStart: () => void }) {
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const about = describe(image);

  async function download() {
    setError(null);
    setBusy(true);
    try {
      const r = await api<{ url: string }>("/api/account/downloads", { json: { file: image.file } });
      setLink(r.url);
      onStart();
      window.location.assign(r.url);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!image.sha256) return;
    await navigator.clipboard.writeText(image.sha256);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <button className="btn btn-primary min-h-14 px-8 text-lg" onClick={() => void download()} disabled={busy}>
          {busy ? "Preparing…" : link ? "Download again" : "Download"}
        </button>
        <p className="text-ink-2">
          Atzmai OS{about.version ? ` ${about.version}` : ""}, released {about.day}. One file of {fmtBytes(image.size)}.
        </p>
      </div>
      {error && <p role="alert" className="text-bad">{error}</p>}
      {link && (
        <p role="status" className="max-w-[38rem] text-ink-2">
          Your download has started. If it stops, <a href={link}>this link</a> picks it up again for 48 hours. While it downloads, get a USB stick of 8 GB or more ready.
        </p>
      )}
      <details className="checksum max-w-[44rem] rounded-2xl bg-orbit-2 shadow-[inset_0_0_0_1px_rgb(255_255_255/.08)]">
        <summary className="cursor-pointer list-none px-5 py-3.5 font-display text-[15px] font-medium text-ink-2 hover:text-ink">
          Check the file is the one we published
        </summary>
        <div className="flex flex-col gap-3 px-5 pb-5">
          {image.sha256 ? (
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 rounded-xl bg-glass px-3 py-2 font-mono text-[13px] break-all text-ink-2">{image.sha256}</code>
              <button className="btn" onClick={() => void copy()}>{copied ? "Copied" : "Copy"}</button>
            </div>
          ) : (
            <p className="text-[15px] text-ink-3">The checksum is published with the release notes.</p>
          )}
          <p className="text-[14px] text-ink-3">
            Your copy's checksum should match. On Linux: <code className="font-mono">sha256sum {about.name}</code>. On a Mac: <code className="font-mono">shasum -a 256 {about.name}</code>. On Windows: <code className="font-mono">certutil -hashfile {about.name} SHA256</code>.
            Updates after install are signed with the Atzmai OS release key, fingerprint <span className="font-mono">3E1B 798A BDE9 8D43 25B2 22D0 EE8B 9AD9 3BEF 84F2</span>.
          </p>
        </div>
      </details>
    </div>
  );
}
